'use client';

/**
 * TableCell Component
 *
 * Universal cell renderer that selects the appropriate cell type based on column configuration.
 *
 * ## CRITICAL: Typography Isolation System
 *
 * This component implements automatic typography wrapping to ensure consistent styling
 * across ALL table states (loading, data, error, empty) while avoiding Tailwind arbitrary
 * value conflicts.
 *
 * ### How It Works:
 *
 * 1. **Built-in Column Types** (text, number, pill, badge, etc.)
 *    - Automatically render with proper inline styles via cell components
 *    - NO consumer action needed - just specify type in column definition
 *
 * 2. **Custom renderCell Functions**
 *    - Returns React element (JSX) → Passed through as-is (element handles own styling)
 *    - Returns primitive (string/number) → AUTO-WRAPPED with textStyles.primary()
 *    - This ensures primitives NEVER render unstyled
 *
 * ### Why This Matters:
 *
 * **Problem:** Tailwind config has `text-base: 13px` override + globals.css has `h1-h6`
 * font-bold overrides. Using Tailwind arbitrary value classes causes massive
 * oversized text due to cascading conflicts.
 *
 * **Solution:** ALL typography uses inline styles from `getTextStyles(theme)`, completely
 * bypassing Tailwind config and global CSS cascading.
 *
 * ### For Future Developers:
 *
 * ❌ **NEVER DO THIS:**
 * ```tsx
 * // In consumer code (views/):
 * import { getTextStyles, defaultTheme } from '@/components/shared/DataTable/theme/tokens';
 *
 * renderCell: (value) => {
 *   const textStyles = getTextStyles(defaultTheme);  // ❌ Manual theme handling
 *   return <span style={textStyles.primary()}>{value}</span>;
 * }
 * ```
 *
 * ✅ **DO THIS INSTEAD:**
 * ```tsx
 * // For simple text in custom layout:
 * import { CellText } from '@/components/shared/DataTable';
 *
 * renderCell: (value) => (
 *   <div className="flex items-center gap-2">
 *     <Icon />
 *     <CellText>{value}</CellText>  // ✅ Helper component
 *   </div>
 * )
 *
 * // For plain primitives:
 * renderCell: (value) => value  // ✅ Auto-wrapped by TableCell
 *
 * // For status pills:
 * renderCell: (value) => <PillCell value={value} variant="active" />  // ✅ Uses helper
 * ```
 *
 * ### Testing Checklist:
 *
 * When modifying this component or adding new pages with DataTable:
 * 1. ✅ Loading skeleton renders with proper typography
 * 2. ✅ Actual data renders with IDENTICAL typography to loading state
 * 3. ✅ Headers are uppercase with subtle letter-spacing
 * 4. ✅ Custom renderCell content has proper base font size/weight
 * 5. ✅ NO consumer code imports theme utilities (getTextStyles, defaultTheme)
 *
 * @see CellText - Helper for text in custom layouts
 * @see PillCell - Helper for status pills
 */

import React from 'react';
import { cn } from '@/lib/utils';
import { ColumnDefinition, DataTableTheme } from './types';
import { getColumnValue } from './utils/sorting';
import { themeClasses, getTextStyles } from './theme/tokens';

// Cell type imports
import { TextCell } from './cells/TextCell';
import { NumberCell } from './cells/NumberCell';
import { BadgeCell, BadgeVariant } from './cells/BadgeCell';
import { Pill, PillVariant } from '@/components/ui/pill';
import { ToggleCell } from './cells/ToggleCell';
import { AvatarTextCell } from './cells/AvatarTextCell';
import { ActionsCell } from './cells/ActionsCell';
import { CellExpandable } from './cells/CellExpandable';

interface TableCellProps<TData = any> {
  row: TData;
  rowIndex: number;
  column: ColumnDefinition<TData>;
  onClick?: () => void;
  className?: string;
  // Expandable row support
  isExpanded?: boolean;
  onToggleExpand?: () => void;
  theme: DataTableTheme;
  // Sticky column support
  isStickyRight?: boolean;
  isStickyLeft?: boolean;
  isSelected?: boolean;
  /**
   * When true, offsets a sticky-left cell by the checkbox column width so it
   * sits to the right of the pinned checkbox column instead of colliding with
   * it at left: 0. Forwarded from DataTable where it knows selectionMode.
   */
  hasLeftCheckboxOffset?: boolean;
  /** Draw gridlines (bottom + right). Forwarded from DataTable. */
  bordered?: boolean;
  /** True for the last row — suppress bottom border so the card edge stays clean. */
  isLastRow?: boolean;
  /** True for the last visible column — suppress right border. */
  isLastColumn?: boolean;
}

const alignClasses = {
  left: 'text-left',
  center: 'text-center',
  right: 'text-right',
};

export function TableCell<TData = any>({
  row,
  rowIndex,
  column,
  onClick,
  className,
  isExpanded,
  onToggleExpand,
  theme,
  isStickyRight = false,
  isStickyLeft = false,
  isSelected = false,
  hasLeftCheckboxOffset = false,
  bordered = false,
  isLastRow = false,
  isLastColumn = false,
}: TableCellProps<TData>) {
  // Direct border classes (bypassing descendant-selector arbitrary variants on
  // the Card). Applied per-cell so specificity is deterministic regardless of
  // sticky wrappers or cell type.
  const borderClasses = bordered
    ? cn(
        !isLastRow && 'border-b border-slate-200',
        !isLastColumn && !isStickyRight && 'border-r border-slate-200'
      )
    : '';
  const textStyles = getTextStyles(theme);
  const shouldAllowVisibleOverflow = column.type === 'actions' || column.id === 'actions';

  // Determine background color for sticky cells to match row state
  const getStickyBackground = () => {
    if (!isStickyRight && !isStickyLeft) return undefined;
    // Match row background: selected > expanded > default
    if (isSelected) return 'var(--dt-bg-row-selected)';
    if (isExpanded) return '#FCFCFC';
    return 'var(--dt-bg-row)';
  };

  // Get sticky styles including shadow.
  //
  // The first solid-color shadow extends the cell's background a few pixels
  // beyond its declared edge on the side that meets a scrolling column. This
  // masks sub-pixel rendering gaps where a non-sticky neighbor's content
  // (e.g. a CompanyLogo) can briefly bleed through between the sticky cell
  // and its scrolling neighbor under `table-auto` layout. The blurred shadow
  // remains as the visual indicator of the sticky boundary.
  const getStickyStyles = () => {
    const bg = getStickyBackground() ?? '#FFFFFF';
    if (isStickyRight) {
      return {
        position: 'sticky' as const,
        right: 0,
        zIndex: 20,
        backgroundColor: bg,
        boxShadow: `-4px 0 0 0 ${bg}, -2px 0 8px rgba(0, 0, 0, 0.12)`,
      };
    }
    if (isStickyLeft) {
      return {
        position: 'sticky' as const,
        // Offset to sit right of the pinned checkbox column when present.
        // Checkbox cell is `w-8` = 32px (border-box), so the NAME column's
        // sticky left edge aligns exactly with the checkbox cell's right edge.
        left: hasLeftCheckboxOffset ? 32 : 0,
        zIndex: 20,
        backgroundColor: bg,
        boxShadow: `4px 0 0 0 ${bg}, 2px 0 8px rgba(0, 0, 0, 0.12)`,
      };
    }
    return {};
  };

  // Custom renderer takes precedence
  if (column.renderCell) {
    const value = getColumnValue(row, column);
    const customContent = column.renderCell(value, row, rowIndex);

    // If custom renderCell returns a React element (JSX), use as-is
    // If it returns primitive (string/number), wrap with base text styling
    const wrappedContent = React.isValidElement(customContent)
      ? customContent
      : <span style={textStyles.primary()}>{customContent}</span>;

    return (
      <td
        className={cn(
          themeClasses.cell,
          column.type === 'expandable' && 'px-2',
          'align-middle',
          !isStickyRight && !isStickyLeft && (shouldAllowVisibleOverflow ? 'overflow-visible' : 'overflow-hidden'),
          alignClasses[column.align || 'left'],
          onClick && 'cursor-pointer',
          isStickyRight && 'border-l border-slate-200',
          isStickyLeft && 'border-r border-slate-200',
          borderClasses,
          className
        )}
        style={{
          width: column.width,
          minWidth: column.minWidth,
          maxWidth: column.maxWidth,
          ...getStickyStyles(),
        }}
        onClick={onClick}
      >
        {wrappedContent}
      </td>
    );
  }

  // Render based on column type
  const cellContent = renderCellByType(row, rowIndex, column, theme, isExpanded, onToggleExpand);

  return (
    <td
      className={cn(
        themeClasses.cell,
        'align-middle',
        !isStickyRight && !isStickyLeft && (shouldAllowVisibleOverflow ? 'overflow-visible' : 'overflow-hidden'),
        alignClasses[column.align || 'left'],
        onClick && 'cursor-pointer',
        isStickyRight && 'border-l border-slate-200',
        isStickyLeft && 'border-r border-slate-200',
        borderClasses,
        className
      )}
      style={{
        width: column.width,
        minWidth: column.minWidth,
        maxWidth: column.maxWidth,
        ...getStickyStyles(),
      }}
      onClick={onClick}
    >
      {cellContent}
    </td>
  );
}

/**
 * Render cell content based on column type
 */
function renderCellByType<TData>(
  row: TData,
  rowIndex: number,
  column: ColumnDefinition<TData>,
  theme: DataTableTheme,
  isExpanded?: boolean,
  onToggleExpand?: () => void
): React.ReactNode {
  const value = getColumnValue(row, column);
  const meta = column.meta || {};

  switch (column.type) {
    case 'text':
      return (
        <TextCell
          value={value}
          truncate={column.truncate}
          wrap={column.wrap}
          align={column.align}
          theme={theme}
        />
      );

    case 'number':
      return (
        <NumberCell
          value={value}
          format={meta.format}
          locale={meta.locale}
          currency={meta.currency}
          decimals={meta.decimals}
          align={column.align || 'right'}
        />
      );

    case 'badge':
      return (
        <BadgeCell
          value={value}
          variant={meta.variant as BadgeVariant}
          icon={meta.icon}
          colorMap={meta.colorMap as Record<string, BadgeVariant>}
        />
      );

    case 'pill':
      return (
        <Pill
          value={value}
          variant={meta.variant as PillVariant}
          icon={meta.icon}
          colorMap={meta.colorMap as Record<string, PillVariant>}
        />
      );

    case 'toggle':
      return (
        <ToggleCell
          value={!!value}
          onChange={(newValue) => {
            if (meta.onChange) {
              return meta.onChange(row, newValue);
            }
          }}
          disabled={meta.disabled ? meta.disabled(row) : false}
          ariaLabel={`Toggle ${column.label} for row ${rowIndex + 1}`}
          align={column.align}
        />
      );

    case 'avatarText': {
      const primaryText = meta.primaryKey
        ? (row as any)[meta.primaryKey]
        : value;
      const secondaryText = meta.secondaryKey
        ? (row as any)[meta.secondaryKey]
        : undefined;
      const avatarUrl = meta.avatarKey
        ? (row as any)[meta.avatarKey]
        : undefined;
      const avatarFallback = meta.avatarFallbackKey
        ? (row as any)[meta.avatarFallbackKey]
        : undefined;

      return (
        <AvatarTextCell
          primaryText={primaryText}
          secondaryText={secondaryText}
          avatarUrl={avatarUrl}
          avatarFallback={avatarFallback}
        />
      );
    }

    case 'actions':
      return (
        <ActionsCell
          actions={meta.actions || []}
          row={row}
          rowIndex={rowIndex}
        />
      );

    case 'expandable':
      return (
        <CellExpandable
          isExpanded={isExpanded || false}
          onToggle={onToggleExpand || (() => {})}
        />
      );

    case 'custom':
      // Custom type requires renderCell
      return value != null ? String(value) : '-';

    default:
      return value != null ? String(value) : '-';
  }
}
